import { useTranslation } from 'react-i18next';
import { X, ChevronUp, Users, Thermometer, Wind, TrendingUp, Shield, Phone, Globe, MapPin, Mail, Printer, User, Activity, Clock, Navigation } from 'lucide-react';
import { CONGESTION_CONFIG } from '../../data/seoulSpots.js';
import styles from './CongestionBottomSheet.module.css';

export default function CongestionBottomSheet({ spot, data, expanded, onToggle, onClose, onRouteClick }) {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const config = CONGESTION_CONFIG[data?.congestionLevel] || {
    color: '#94a3b8', bgColor: '#f1f5f9',
    label: { ko: '정보없음', en: 'N/A' },
  };

  // data.congestionLevel 자체가 1~5의 숫자이므로 그대로 사용합니다.
  const levelIndex = data?.congestionLevel || 0;
  
  const isSafetySpot = ['police', 'hospital', 'embassy'].includes(spot.category);

  return (
    <div className={`bottom-sheet ${expanded ? styles.expanded : styles.collapsed}`}>
      {/* 핸들 영역 */}
      <div className={styles.header} onClick={onToggle} role="button" aria-label="시트 토글">
        <div className="bottom-sheet-handle" />
        <div className={styles.headerContent}>
          <div className={styles.spotInfo}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <h2 className={styles.spotName}>{(isEnglish && spot.en) ? spot.en : (spot.name || data?.areaName)}</h2>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button
              className={`btn-icon ${styles.toggleBtn}`}
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
              style={{ width: 32, height: 32 }}
              aria-label={expanded ? "상세 정보 접기" : "상세 정보 펼치기"}
            >
              <ChevronUp
                size={16}
                style={{ transform: expanded ? 'rotate(180deg)' : '', transition: 'transform 0.3s' }}
              />
            </button>
            <button
              className={`btn-icon ${styles.closeBtn}`}
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              style={{ width: 32, height: 32 }}
              aria-label="장소 정보 닫기"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* 시트 내용 */}
      <div className={styles.content}>
        {isSafetySpot ? (
          <div className={styles.section} style={{ padding: '0', textAlign: 'left' }}>
            {/* 기본 이미지 설정 (카테고리별 분기) */}
            <div style={{
              width: '100%', height: '180px',
              backgroundColor: '#f1f5f9',
              backgroundImage: `url(${
                spot.category === 'police' ? 'https://images.unsplash.com/photo-1555819206-7b30da4f1506?auto=format&fit=crop&q=80&w=800' :
                spot.category === 'hospital' ? 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=800' :
                'https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=800'
              })`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '12px',
              marginBottom: '16px',
              marginTop: '8px'
            }} />
            
            <p style={{ fontWeight: '700', color: '#334155', fontSize: '1.05rem', marginBottom: 16 }}>
              {spot.category === 'police' ? t('safety.policeTitle') : spot.category === 'hospital' ? t('safety.hospitalTitle') : t('safety.embassyTitle')}
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              {spot.ambassador && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#475569', fontSize: '0.9rem' }}>
                  <User size={16} color="#64748b" />
                  <span>{spot.ambassador} (대사)</span>
                </div>
              )}
              {spot.emclsName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#0ea5e9', fontSize: '0.9rem', fontWeight: '600' }}>
                  <Shield size={16} color="#0ea5e9" />
                  <span>{spot.emclsName}</span>
                </div>
              )}
              {spot.dutyTime && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#475569', fontSize: '0.9rem' }}>
                  <Clock size={16} color="#64748b" />
                  <span>{spot.dutyTime}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#475569', fontSize: '0.9rem' }}>
                <Phone size={16} color="#64748b" />
                <span>{spot.phone || (spot.category === 'police' ? t('safety.defaultPhonePolice') : spot.category === 'hospital' ? t('safety.defaultPhoneHospital') : t('safety.defaultPhoneEmbassy'))}</span>
              </div>
              {spot.category !== 'hospital' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#475569', fontSize: '0.9rem' }}>
                  <Globe size={16} color="#64748b" />
                  <a href={spot.website || (spot.category === 'police' ? 'https://www.police.go.kr' : 'https://www.mofa.go.kr')} target="_blank" rel="noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>
                    {t('safety.visitWebsite')}
                  </a>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#475569', fontSize: '0.9rem' }}>
                <MapPin size={16} color="#64748b" />
                <span>{spot.address || t('safety.addressPlaceholder')}</span>
              </div>
              {spot.emergencyRoom !== undefined && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: spot.emergencyRoom ? '#ef4444' : '#475569', fontSize: '0.9rem', fontWeight: spot.emergencyRoom ? '600' : '400' }}>
                  <Activity size={16} color={spot.emergencyRoom ? '#ef4444' : '#64748b'} />
                  <span>{spot.emergencyRoom ? '응급실 운영' : '응급실 미운영'}</span>
                </div>
              )}
              {spot.email && spot.email.trim() !== '-' && spot.email.trim() !== '' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#475569', fontSize: '0.9rem' }}>
                  <Mail size={16} color="#64748b" />
                  <a href={`mailto:${spot.email}`} style={{ color: '#475569', textDecoration: 'none' }}>{spot.email}</a>
                </div>
              )}
              {spot.fax && spot.fax.trim() !== '-' && spot.fax.trim() !== '' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#475569', fontSize: '0.9rem' }}>
                  <Printer size={16} color="#64748b" />
                  <span>Fax: {spot.fax}</span>
                </div>
              )}
            </div>
            
            <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '12px', border: '1px solid #bfdbfe' }}>
              <p style={{ fontSize: '0.85rem', lineHeight: 1.6, color: '#1e40af' }}>
                <strong>{t('safety.noticeTitle')}</strong> {t('safety.noticeDesc')}
              </p>
            </div>
          </div>
        ) : data ? (
          <>
            {/* 인구 정보 */}
            <div className={styles.section}>
              <div className={styles.statRow}>
                <div className={styles.stat}>
                  <Users size={16} color={config.color} />
                  <div>
                    <div className={styles.statLabel}>{t('congestion.populationLabel', { defaultValue: '추정 인구' })}</div>
                    <div className={styles.statValue}>
                      {t('congestion.population', { 
                        min: isEnglish ? (data.populationMin / 1000).toFixed(1) : (data.populationMin / 10000).toFixed(1), 
                        max: isEnglish ? (data.populationMax / 1000).toFixed(1) : (data.populationMax / 10000).toFixed(1) 
                      })}
                    </div>
                  </div>
                </div>

                {/* 혼잡도 게이지 */}
                <div className={styles.gauge}>
                  {[1, 2, 3, 4, 5].map(i => (
                    <div
                      key={i}
                      className={styles.gaugeBar}
                      style={{
                        background: i <= levelIndex ? config.color : 'var(--color-border)',
                        opacity: i <= levelIndex ? 1 - (levelIndex - i) * 0.1 : 0.3,
                      }}
                    />
                  ))}
                </div>
              </div>

              {data.congestionLevel && (
                <p className={styles.message}>
                  {t(`congestion.msgLevel${data.congestionLevel}`, { defaultValue: data.congestionMessage })}
                </p>
              )}
            </div>

            {/* 날씨 & 교통 */}
            {(data.weather?.temp !== '-' || data.traffic?.index) && (
              <div className={styles.section}>
                <div className={styles.infoGrid}>
                  {data.weather?.temp !== '-' && (
                    <div className={styles.infoItem}>
                      <Thermometer size={14} color="var(--color-sky)" />
                      <span>{data.weather.temp}°C</span>
                    </div>
                  )}
                  {data.weather?.humidity !== '-' && (
                    <div className={styles.infoItem}>
                      <Wind size={14} color="var(--color-sky)" />
                      <span>{t('weather.humidity')} {data.weather.humidity}%</span>
                    </div>
                  )}
                  {data.weather?.pm10Index && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoIcon}>🌫️</span>
                      <span>{t('weather.pm10')} {data.weather.pm10Index}</span>
                    </div>
                  )}
                  {data.traffic?.index && (
                    <div className={styles.infoItem}>
                      <TrendingUp size={14} color="var(--color-sky)" />
                      <span>{t('traffic.title')} {data.traffic.index}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 예보 */}
            {data.forecast?.length > 0 && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>{t('congestion.forecast')}</h3>
                <div className={styles.forecastRow}>
                  {data.forecast.slice(0, 6).map(f => {
                    // 예보 레벨(문자열)을 숫자 키로 매핑
                    let lvlNum = 1;
                    if (f.level === '여유') lvlNum = 1;
                    else if (f.level === '보통') lvlNum = 2;
                    else if (f.level === '약간 붐빔') lvlNum = 3;
                    else if (f.level === '붐빔') lvlNum = 4;
                    else if (f.level === '매우 붐빔') lvlNum = 5;
                    
                    const fc = CONGESTION_CONFIG[lvlNum];
                    const hour = f.time?.split(' ')[1]?.slice(0, 5);
                    return (
                      <div key={f.time} className={styles.forecastItem}>
                        <span className={styles.forecastTime}>{hour}</span>
                        <span
                          className={styles.forecastDot}
                          style={{ background: fc?.color || '#94a3b8' }}
                        />
                        <span className={styles.forecastLabel}>{t(`congestion.level${lvlNum}`)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 행사 정보 */}
            {data.events?.length > 0 && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  {t('congestion.eventsTitle', { defaultValue: '진행 중인 행사' })} <span role="img" aria-label="party">🎉</span>
                </h3>
                <div className={styles.eventList}>
                  {data.events.map((ev, idx) => (
                    <div key={idx} className={styles.eventItem}>
                      <div className={styles.eventName}>{ev.name}</div>
                      <div className={styles.eventDetails}>
                        <div className={styles.eventInfoLine}>
                          <span className={styles.eventInfoIcon}>📅</span>
                          <span>{ev.period}</span>
                        </div>
                        <div className={styles.eventInfoLine}>
                          <span className={styles.eventInfoIcon}>📍</span>
                          <span>{ev.place}</span>
                        </div>
                      </div>
                      <div className={styles.eventFooter}>
                        {ev.isFree && <span className={styles.freeBadge}>{t('congestion.free', { defaultValue: '무료' })}</span>}
                        {ev.url && (
                          <a 
                            href={ev.url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className={styles.eventLink}
                          >
                            {t('congestion.detail', { defaultValue: '상세보기' })}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.updatedAt && (
              <p className={styles.updatedAt}>
                {t('congestion.updatedAt', { time: data.updatedAt })} {data.isMock && t('congestion.demoData', { defaultValue: '(데모 데이터)' })}
              </p>
            )}
          </>
        ) : (
          <div className={styles.noData}>
            <div className="spinner" />
            <span>{t('map.loading')}</span>
          </div>
        )}
      </div>

      <div className={styles.floatingAction}>
        <button 
          className={styles.routeBtn}
          onClick={(e) => { e.stopPropagation(); onRouteClick?.(); }}
        >
          <Navigation size={18} />
          {t('home.findRoute', { defaultValue: '이 장소로 길찾기' })}
        </button>
      </div>
    </div>
  );
}
